import React, { useState, useCallback } from 'react';
import { useDropzone, FileRejection, ErrorCode } from 'react-dropzone';
import { FiUploadCloud, FiFile, FiX, FiAlertCircle } from 'react-icons/fi';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useConversationStore } from '../../stores/conversationStore';

interface FileUploadProps {
  conversationId: string;
  onUploadComplete: (fileKey: string, fileName: string) => void;
}

// File upload restrictions
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'text/plain',
  'text/csv',
  'application/json',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const ALLOWED_EXTENSIONS = [
  'jpg', 'jpeg', 'png', 'gif', 'webp',
  'pdf', 'txt', 'csv', 'json',
  'doc', 'docx', 'xls', 'xlsx'
];

const FileUpload: React.FC<FileUploadProps> = ({ conversationId, onUploadComplete }) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [validationError, setValidationError] = useState<string | null>(null);

  const { uploadAttachment } = useConversationStore();

  /**
   * Validates file extension matches MIME type
   */
  const validateFile = (file: File): { valid: boolean; error?: string } => {
    const extension = file.name.split('.').pop()?.toLowerCase();

    if (!extension) {
      return { valid: false, error: 'File has no extension' };
    }

    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      return {
        valid: false,
        error: `File type .${extension} not allowed. Allowed types: ${ALLOWED_EXTENSIONS.join(', ')}`
      };
    }

    // Verify extension matches MIME type
    const mimeToExtension: Record<string, string[]> = {
      'image/jpeg': ['jpg', 'jpeg'],
      'image/png': ['png'],
      'image/gif': ['gif'],
      'image/webp': ['webp'],
      'application/pdf': ['pdf'],
      'text/plain': ['txt'],
      'text/csv': ['csv'],
      'application/json': ['json'],
      'application/msword': ['doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['docx'],
      'application/vnd.ms-excel': ['xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['xlsx'],
    };

    const expectedExtensions = mimeToExtension[file.type];
    if (expectedExtensions && !expectedExtensions.includes(extension)) {
      return {
        valid: false,
        error: `File extension .${extension} does not match file type ${file.type}`
      };
    }

    return { valid: true };
  };

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
    setValidationError(null);

    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      const error = rejection.errors[0];

      if (error.code === 'file-too-large') {
        setValidationError(`File "${rejection.file.name}" is too large. Maximum size is 10MB.`);
        toast.error(`File too large (max 10MB)`);
      } else if (error.code === 'file-invalid-type') {
        setValidationError(`File type "${rejection.file.type}" is not allowed.`);
        toast.error('File type not allowed');
      } else {
        setValidationError(error.message);
        toast.error('File rejected');
      }
      return;
    }

    if (acceptedFiles.length > 0) {
      const selectedFile = acceptedFiles[0];
      const validation = validateFile(selectedFile);

      if (!validation.valid) {
        setValidationError(validation.error || 'Invalid file');
        toast.error(validation.error || 'Invalid file');
        return;
      }

      setFile(selectedFile);
      setValidationError(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    maxSize: MAX_FILE_SIZE,
    accept: ALLOWED_MIME_TYPES.reduce((acc, type) => ({
      ...acc,
      [type]: []
    }), {} as Record<string, string[]>),
  });

  const handleUpload = async () => {
    if (!file) return;
    await uploadAttachment(conversationId, file);
    setIsUploading(false);
    setFile(null);
  };

  return (
    <div className="p-4">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
          ${isDragActive ? 'border-blue-500 bg-blue-50' : validationError ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
      >
        <input {...getInputProps()} />
        <FiUploadCloud className="mx-auto h-12 w-12 text-gray-400" />
        <p className="mt-2 text-sm text-gray-600">
          {isDragActive ? 'Drop the file here...' : 'Drag & drop a file here, or click to select'}
        </p>
        <p className="mt-1 text-xs text-gray-500">
          Max 10MB • Allowed: JPG, PNG, PDF, TXT, CSV, DOC, XLS
        </p>
      </div>

      {/* Validation Error */}
      {validationError && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <FiAlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">File Validation Error</p>
            <p className="text-xs text-red-600 mt-1">{validationError}</p>
          </div>
        </div>
      )}

      {file && !isUploading && (
        <div className="mt-4">
          <div className="flex items-center justify-between p-2 bg-gray-100 rounded-lg">
            <div className="flex items-center gap-2">
              <FiFile className="text-gray-500" />
              <span className="text-sm">{file.name}</span>
            </div>
            <button onClick={() => setFile(null)}>
              <FiX className="text-gray-500 hover:text-red-500" />
            </button>
          </div>
          <button
            onClick={handleUpload}
            className="w-full mt-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Upload and Send
          </button>
        </div>
      )}

      {isUploading && (
        <div className="mt-4">
          <div className="flex justify-between mb-1">
            <span className="text-sm font-medium text-gray-900">{file?.name}</span>
            <span className="text-sm font-medium text-gray-900">{uploadProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-blue-600 h-2.5 rounded-full"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
